import FormattedMessage from 'I18n/components/FormattedMessage';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getUserInfo } from 'ui-addon-upgrades/_core/common/api/getUserInfo';
import UIFloatingAlertList from 'UIComponents/alert/UIFloatingAlertList';
import withErrorBoundary from '../../ErrorBoundary';
import { RequestAccessModalWrapper } from '../../RequestAccessModalWrapper';
import { isAdmin } from '../../_core/common/eligibility/isAdmin';
import { navigateTo } from '../../_core/common/navigation/url';
import { requestSKU } from '../../_core/upgrade/requestSKU';
import { generateCheckoutUrl } from './buy-now-api';
import { getBuyNowUsageTrackingProperties, trackClickedBuyNowButton, trackViewBuyNowButton } from './buy-now-trackers';

/**
 * @temporary Remove when all consumers are migrated to UIBuyNowButtonProps
 * @expiration 2025-03-31
 * @deprecated
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const _UIBuyNowButton = ({
  apiNames,
  requestedProducts,
  className,
  disabled = false,
  FailedPurchaseOrderCallToAction,
  onBuyNowFailure,
  onClick,
  shouldDisplayAlertOnFailure = false,
  terms,
  trackingProperties,
  use = 'primary',
  userInfo: passedUserInfo
}) => {
  var _requestedProducts$;
  const buyNowUsageTrackingProperties = getBuyNowUsageTrackingProperties(trackingProperties);
  const requestAccessModalTrackingProperties = Object.assign({}, buyNowUsageTrackingProperties, {
    modalType: 'requestSKU'
  });
  const [userInfo, setUserInfo] = useState(passedUserInfo);
  const [isLoadingUserState, setIsLoadingUserState] = useState(!passedUserInfo);
  const [isGeneratingPurchaseOrderId, setIsGeneratingPurchaseOrderId] = useState(false);
  const [localAlertStore] = useState(() => {
    return FloatingAlertStore.newFloatingAlertStore();
  });
  const hasPermissionToBuyNow = useMemo(() => {
    return !!userInfo && isAdmin(userInfo.user, 'billing');
  }, [userInfo]);

  // Temporary fallback for clients that call this component with apiNames, not products
  const _requestedProducts = requestedProducts || (apiNames === null || apiNames === void 0 ? void 0 : apiNames.map(apiName => ({
    productApiName: apiName,
    quantity: 1,
    quantityPacks: []
  })));
  const firstApiName = _requestedProducts === null || _requestedProducts === void 0 || (_requestedProducts$ = _requestedProducts[0]) === null || _requestedProducts$ === void 0 ? void 0 : _requestedProducts$.productApiName;
  const handleRequestAccess = useCallback(() => {
    return requestSKU(firstApiName);
  }, [firstApiName]);
  const handleClickBuyNow = useCallback(() => {
    setIsGeneratingPurchaseOrderId(true);
    if (onClick) {
      onClick();
    }
    trackClickedBuyNowButton(buyNowUsageTrackingProperties);
    generateCheckoutUrl(_requestedProducts, terms, buyNowUsageTrackingProperties).then(checkoutUrl => {
      if (checkoutUrl) {
        navigateTo(checkoutUrl);
      }
    }).catch(error => {
      if (shouldDisplayAlertOnFailure) {
        localAlertStore.addAlert({
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "upgrades.buyNowFailureAlert.title"
          }),
          message: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "upgrades.buyNowFailureAlert.message"
            }), FailedPurchaseOrderCallToAction || null]
          }),
          type: 'danger'
        });
      }
      if (onBuyNowFailure) {
        onBuyNowFailure(error);
      }
    }).finally(() => {
      setIsGeneratingPurchaseOrderId(false);
    });
  }, [onClick, buyNowUsageTrackingProperties, _requestedProducts, terms, shouldDisplayAlertOnFailure, onBuyNowFailure, localAlertStore, FailedPurchaseOrderCallToAction]);
  useEffect(() => {
    if (!passedUserInfo) {
      void getUserInfo().then(setUserInfo).finally(() => setIsLoadingUserState(false));
    }
  }, [passedUserInfo]);
  useEffect(() => {
    trackViewBuyNowButton(buyNowUsageTrackingProperties);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (isLoadingUserState) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UIFloatingAlertList, {
      alertStore: localAlertStore
    }), hasPermissionToBuyNow ? /*#__PURE__*/_jsx(UILoadingButton, {
      "data-test-id": "buy-now-button",
      className: className,
      disabled: disabled,
      loading: isGeneratingPurchaseOrderId,
      use: use,
      onClick: handleClickBuyNow,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.checkoutButton.default"
      })
    }) : /*#__PURE__*/_jsx(RequestAccessModalWrapper, {
      buttonUse: use,
      floatingAlertStore: localAlertStore,
      requestAccessPromise: handleRequestAccess,
      usageTrackingProperties: requestAccessModalTrackingProperties,
      OpenModalButtonLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.checkoutButton.default"
      }),
      ModalBody: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.requestAccessModalWrapper.buyNow.description"
      }),
      ModalTitle: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.requestAccessModalWrapper.buyNow.title"
      }),
      modalUse: "success",
      modalWidth: 540,
      SubmitRequestButtonLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.requestAccessModalWrapper.buyNow.button"
      })
    })]
  });
};
export const UIBuyNowButton = withErrorBoundary(_UIBuyNowButton, {
  boundaryName: 'UIBuyNowButton'
});